// Iter 130 — Forced-change-password page.
// Shown when a user logs in after an admin-initiated reset
// (must_change_password=true). Cannot navigate anywhere else until done.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Loader2, ShieldCheck, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePasswordPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (newPw.length < 8) { setErr('Password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setErr('New password and confirmation do not match'); return; }
    if (newPw === oldPw) { setErr('Pick something different from the temporary password'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { old_password: oldPw, new_password: newPw },
        { withCredentials: true });
      if (refresh) await refresh();
      navigate('/', { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not update password');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10" data-testid="change-password-page">
      <div className="w-full max-w-md bg-card border rounded-2xl shadow-xl p-7">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-[#0d2644] text-white flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#0d2644' }}>
              {forced ? 'Choose a new password' : 'Change your password'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {forced
                ? 'Your password was reset by an administrator. Pick a new one to continue.'
                : 'Update your i3 staff portal password.'}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3 mt-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: '#0d2644' }}>
              {forced ? 'Temporary password (from email)' : 'Current password'}
            </label>
            <input type={show ? 'text' : 'password'} value={oldPw} onChange={e => setOldPw(e.target.value)}
                   required autoFocus data-testid="change-pw-old"
                   className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2644]/30" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: '#0d2644' }}>
              New password <span className="text-muted-foreground font-normal lowercase tracking-normal">(min 8 chars)</span>
            </label>
            <input type={show ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
                   required minLength={8} data-testid="change-pw-new"
                   className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2644]/30" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1.5 block" style={{ color: '#0d2644' }}>
              Confirm new password
            </label>
            <input type={show ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                   required data-testid="change-pw-confirm"
                   className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2644]/30" />
          </div>

          <button type="button" onClick={() => setShow(s => !s)}
                  className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
            {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {show ? 'Hide passwords' : 'Show passwords'}
          </button>

          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="change-pw-error">
              {err}
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="change-pw-submit"
                  className="w-full mt-2 bg-[#0d2644] text-white font-semibold py-2.5 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {busy ? 'Updating…' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  );
}
